import { Component, OnInit, effect, input, output, signal, inject } from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {
  ECookieType,
  ICookieConsentForm,
  ICookieConsentFormValue,
} from '@app/cookie-consent/models/cookie-consent.model';
import {CookieManageService} from '@app/cookie-consent/services/cookie.service';
import {environment} from '@environments/environment';
import {TranslateModule} from '@ngx-translate/core';
import {ButtonModule} from 'primeng/button';
import {CheckboxModule} from 'primeng/checkbox';
import {DividerModule} from 'primeng/divider';

@Component({
  selector: 'app-cookie-consent-form',
  imports: [ButtonModule, DividerModule, CheckboxModule, TranslateModule, ReactiveFormsModule],
  templateUrl: './cookie-consent-form.component.html',
  styleUrls: ['./cookie-consent-form.component.scss'],
})
export class CookieConsentFormComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly cookieService = inject(CookieManageService);

  readonly shouldShowSettings = input(false);
  readonly _shouldShowSettings = signal(false);

  readonly showMoreInformation = output<ECookieType>();
  readonly hideConsentModal = output<void>();
  readonly shouldShowAllowSelection = output<boolean>();

  cookieForm: FormGroup<ICookieConsentForm>;
  icon = 'pi pi-plus';
  ECookieTypes = ECookieType;

  constructor() {
    effect(() => {
      this._shouldShowSettings.set(this.shouldShowSettings());
    });
    this.cookieForm = this.formBuilder.group({
      necessary: [true, Validators.required],
      performance: [false],
      targeting: [false],
      functionality: [false],
      unclassified: [false],
    });

    this.cookieForm.controls.necessary.disable();
  }

  get cookieFormControls() {
    return this.cookieForm.controls;
  }

  ngOnInit(): void {
    this.cookieForm.valueChanges.subscribe(value => {
      const areOptionalCookiesChecked =
        !!value.performance || !!value.targeting || !!value.functionality || !!value.unclassified;
      this.shouldShowAllowSelection.emit(areOptionalCookiesChecked);
    });
  }

  onToggleSettings(): void {
    this._shouldShowSettings.set(!this._shouldShowSettings());
    this.icon = this._shouldShowSettings() ? 'pi pi-minus' : 'pi pi-plus';
  }

  onAllowAllCookies(): void {
    this.cookieFormControls.necessary.setValue(true);
    this.cookieFormControls.performance.setValue(true);
    this.cookieFormControls.targeting.setValue(true);
    this.cookieFormControls.functionality.setValue(true);
    this.cookieFormControls.unclassified.setValue(true);

    const formValue = this.cookieForm.getRawValue() as ICookieConsentFormValue;

    this.saveCookies(formValue);
    this.updateCookieConsentState(formValue);
    this.hideCookieConsentModal();
  }

  onAllowNecessaryCookies(): void {
    this.cookieFormControls.necessary.setValue(true);
    this.cookieFormControls.performance.setValue(false);
    this.cookieFormControls.targeting.setValue(false);
    this.cookieFormControls.functionality.setValue(false);
    this.cookieFormControls.unclassified.setValue(false);

    const formValue = this.cookieForm.getRawValue() as ICookieConsentFormValue;

    this.saveCookies(formValue);
    this.updateCookieConsentState(formValue);
    this.hideCookieConsentModal();
  }

  onAllowSelectionCookies(): void {
    const formValue = this.cookieForm.getRawValue() as ICookieConsentFormValue;

    formValue.necessary = true;
    this.saveCookies(formValue);
    this.hideCookieConsentModal();

    if (formValue.performance || formValue.targeting || formValue.functionality || formValue.unclassified) {
      this.updateCookieConsentState(formValue);
    }
  }

  onFurtherInformation(type: ECookieType): void {
    this.showMoreInformation.emit(type);
  }

  private saveCookies(formValue: ICookieConsentFormValue): void {
    this.cookieService.set<ICookieConsentFormValue>(environment.cookie.cookieConsent, formValue);
  }

  private updateCookieConsentState(formValue: ICookieConsentFormValue): void {
    this.cookieService.cookieConsentState = formValue;
  }

  private hideCookieConsentModal(): void {
    this.hideConsentModal.emit();
  }
}
